import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, CircleNotch, Eye, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { MailConnectionCard } from "@/components/MailConnectionCard";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichEmailEditor } from "@/components/RichEmailEditor";
import { api, fmtDateUK } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const parse = (s) => [...new Set(s.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
const timeNow = () => new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

function Field({ label, htmlFor, children, hint }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#475569]">{label}</label>
      {children}
      {hint}
    </div>
  );
}

export default function FlashEmailDraft() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [subject, setSubject] = useState("");
  const [to, setTo] = useState("");
  const [saveDefault, setSaveDefault] = useState(true);
  const [saveState, setSaveState] = useState("");
  const [busy, setBusy] = useState("");
  const body = useRef("");
  const dirty = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get(`/flash-email/drafts/${draftId}`).then(({ data }) => {
      setDraft(data);
      setSubject(data.subject);
      setTo((data.recipients || []).join(", "));
      body.current = data.body_html;
    }).catch(() => toast.error("Draft not found"));
  }, [draftId]);

  const emails = parse(to);
  const invalid = emails.filter((e) => !EMAIL_RE.test(e));
  const sent = draft?.status === "sent";
  const onMailStatus = useCallback((s) => setDraft((d) => d && ({ ...d, email_configured: s.status === "connected" })), []);

  const save = useCallback(async () => {
    if (!dirty.current || sent) return true;
    clearTimeout(timer.current);
    setSaveState("saving");
    dirty.current = false;
    try {
      await api.put(`/flash-email/drafts/${draftId}`, {
        subject, body_html: body.current, recipients: emails.filter((e) => EMAIL_RE.test(e)),
      });
      setSaveState(dirty.current ? "unsaved" : `Saved ${timeNow()}`);
      return true;
    } catch (e) {
      dirty.current = true;
      setSaveState("error");
      toast.error(e?.response?.data?.detail || "Couldn't save the draft");
      return false;
    }
  }, [draftId, subject, emails, sent]);

  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => () => { saveRef.current(); }, []);

  const touch = () => {
    dirty.current = true;
    setSaveState("unsaved");
  };
  useEffect(() => {
    if (saveState !== "unsaved") return undefined;
    timer.current = setTimeout(save, 1500);
    return () => clearTimeout(timer.current);
  }, [saveState, subject, to, save]);

  const preview = async () => {
    const tab = window.open("", "_blank");
    if (!(await save())) { tab?.close(); return; }
    const r = await api.get(`/flash-email/drafts/${draftId}/preview`, { responseType: "blob" });
    tab.location.href = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
  };

  const send = async () => {
    setBusy("send");
    try {
      dirty.current = true;
      if (!(await save())) return;
      const { data } = await api.post(`/flash-email/drafts/${draftId}/send`, { save_as_default: saveDefault });
      toast.success(`Flash report emailed to ${data.recipients.length} recipient${data.recipients.length > 1 ? "s" : ""}`);
      setDraft((d) => ({ ...d, status: "sent", sent_at: data.sent_at }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the email");
    } finally { setBusy(""); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this draft?")) return;
    dirty.current = false;
    await api.delete(`/flash-email/drafts/${draftId}`);
    toast.success("Draft deleted");
    navigate(`/admin/clients/${draft.client_id}`);
  };

  if (!draft) return <AppShell title="Loading draft…" />;

  return (
    <AppShell
      subtitle={`Email draft · ${draft.period_label}`}
      title={draft.client_name}
      actions={
        <Link to={`/admin/clients/${draft.client_id}`} className="text-sm text-[#475569] hover:text-[#0F172A] flex items-center gap-2" data-testid="draft-back-link">
          <ArrowLeft size={16} /> Back to client
        </Link>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6 items-start">
        <aside className="bg-white border border-[#E2E8F0] rounded-lg card-fade p-6 space-y-5 xl:sticky xl:top-6" data-testid="draft-sidebar">
          <div className="flex items-center justify-between">
            <span className={`text-[11px] uppercase tracking-[0.18em] font-semibold ${sent ? "text-[#059669]" : "text-[#B45309]"}`} data-testid="draft-status">
              {sent ? `Sent ${fmtDateUK(draft.sent_at)}` : "Draft"}
            </span>
            <span className="text-[11px] font-mono text-[#94A3B8]" data-testid="draft-save-state">
              {saveState === "saving" ? "Saving…" : saveState === "unsaved" ? "Unsaved changes" : saveState === "error" ? "Save failed" : saveState}
            </span>
          </div>
          {!sent && <MailConnectionCard onStatus={onMailStatus} />}
          <Field label="To" htmlFor="draft-to" hint={
            <p className="text-xs text-[#64748B]" data-testid="draft-recipients-count">
              {invalid.length ? <span className="text-red-600">Check: {invalid.join(", ")}</span> : `${emails.length} recipient${emails.length === 1 ? "" : "s"} · separate with commas`}
            </p>
          }>
            <Textarea id="draft-to" rows={3} value={to} disabled={sent} onChange={(e) => { setTo(e.target.value); touch(); }}
              placeholder="owner@restaurant.co.uk, gm@restaurant.co.uk" className="font-mono text-xs" data-testid="draft-recipients-input" />
          </Field>
          <Field label="Subject" htmlFor="draft-subject">
            <Input id="draft-subject" value={subject} disabled={sent} onChange={(e) => { setSubject(e.target.value); touch(); }} data-testid="draft-subject-input" />
          </Field>
          {!sent && (
            <label className="flex items-center gap-2 text-sm text-[#334155]">
              <Checkbox checked={saveDefault} onCheckedChange={(v) => setSaveDefault(!!v)} data-testid="draft-save-default" /> Save as this client's default recipients
            </label>
          )}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button variant="outline" onClick={preview} data-testid="draft-preview-btn"><Eye size={14} className="mr-1.5" />Preview</Button>
            {!sent ? (
              <Button variant="outline" onClick={() => { dirty.current = true; save(); }} data-testid="draft-save-btn">
                <CheckCircle size={14} className="mr-1.5" />Save draft
              </Button>
            ) : <span />}
            {!sent && (
              <Button onClick={send} className="col-span-2 bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="draft-send-btn"
                disabled={!!busy || !draft.email_configured || !emails.length || invalid.length > 0 || !subject.trim()}>
                {busy === "send" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <PaperPlaneTilt size={14} className="mr-1.5" />}Send email
              </Button>
            )}
            <Button variant="ghost" onClick={remove} className="col-span-2 text-red-700 hover:bg-red-50" data-testid="draft-delete-btn">
              <Trash size={14} className="mr-1.5" />Delete {sent ? "record" : "draft"}
            </Button>
          </div>
        </aside>
        <section>
          <p className="text-xs text-[#64748B] mb-3">
            {sent ? "This email has been sent and can no longer be edited." : "Click anywhere in the email to change wording, add notes or new paragraphs. Changes save automatically."}
          </p>
          <RichEmailEditor initialHtml={draft.body_html} readOnly={sent} onChange={(html) => { body.current = html; touch(); }} />
        </section>
      </div>
    </AppShell>
  );
}
